// Through the accessor every test mock of fetchWrapper.js provides; it is
// the same singleton, not a React hook.
import { useFetchWrapper as getFetchWrapper } from './fetchWrapper.js';
import config from '../config.js';

/*
 * Operator feature flags, as the browser is allowed to see them (ENG-783).
 *
 * ## The contract
 *
 *   GET /public/features                                      [ENG-782]
 *     public, no auth. → 200 `{"radio": bool, "lyrics": bool}`, both keys
 *     always booleans; payment flags are never included. `Cache-Control:
 *     public, max-age=30` when uncredentialed, `no-store` when a bearer is
 *     sent. The values come from SSM `/songshake/features/{name}/enabled`
 *     (ENG-778), so an operator toggle reaches the API within about 90s.
 *
 * ## Fail closed (Tim Withers, 2026-10-20)
 *
 * Every flag is `false` until the answer is in, and stays `false` if it never
 * comes: a failed request, a non-200 (a backend without the route answers
 * 404; a 429), a body that is not an object, a missing key, or any value other
 * than the literal `true`. Unknown keys are ignored.
 *
 * ## One request per page load
 *
 * Every caller shares one in-flight request and the answer is kept for the
 * life of the page; there is no polling, so a reload picks up a toggle. On
 * failure it is retried twice, at 5s and then 30s, and then not again.
 *
 * React components read it through `useFeatureFlag(name)`
 * (`hooks/useFeatureFlag.js`). The lyrics flag gates listener surfaces only
 * (Tim Withers, 2026-10-23): band/streamer-facing lyrics never read it.
 */

/** Every flag this page knows about, all off. */
const ALL_OFF = Object.freeze({ radio: false, lyrics: false });
const FLAG_NAMES = Object.freeze(Object.keys(ALL_OFF));

export const FeaturesStatus = Object.freeze({
  LOADING: 'loading',
  READY: 'ready',
  FAILED: 'failed',
});

/** When to ask again after a failed request: twice, backing off, then stop. */
export const FEATURES_RETRY_DELAYS_MS = Object.freeze([5000, 30000]);

/** The body read strictly: a flag is on only when its value is exactly `true`. */
export const parseFeatures = (body) => {
  const flags = { ...ALL_OFF };
  if (!body || typeof body !== 'object' || Array.isArray(body)) return flags;
  for (const name of FLAG_NAMES) flags[name] = body[name] === true;
  return flags;
};

const makeSnapshot = (status, flags, disabled = {}) => {
  const effective = { ...flags };
  // A flag an endpoint has answered `feature_disabled` for stays off.
  for (const name of FLAG_NAMES) if (disabled[name]) effective[name] = false;
  return Object.freeze({
    status,
    flags: Object.freeze(effective),
    disabled: Object.freeze({ ...disabled }),
  });
};

let snapshot = makeSnapshot(FeaturesStatus.LOADING, ALL_OFF);
let inFlight = null;
let started = false;
let retries = 0;
let retryTimer = null;
// Bumped by clearFeaturesCache, so a request from before a reset cannot
// publish into the fresh state.
let generation = 0;
const listeners = new Set();

const publish = (next) => {
  snapshot = next;
  listeners.forEach((listener) => listener());
};

const request = () => {
  if (inFlight) return inFlight;
  const mine = generation;
  inFlight = getFetchWrapper()
    .get(`${config.apiBaseUrl}/public/features`)
    .then(
      (body) => {
        if (mine !== generation) return;
        publish(makeSnapshot(FeaturesStatus.READY, parseFeatures(body), snapshot.disabled));
      },
      () => {
        if (mine !== generation) return;
        publish(makeSnapshot(FeaturesStatus.FAILED, ALL_OFF, snapshot.disabled));
        if (retries < FEATURES_RETRY_DELAYS_MS.length) {
          const delay = FEATURES_RETRY_DELAYS_MS[retries];
          retries += 1;
          retryTimer = setTimeout(() => {
            retryTimer = null;
            request();
          }, delay);
        }
      }
    )
    .finally(() => {
      if (mine === generation) inFlight = null;
    });
  return inFlight;
};

/**
 * Starts the one features request for this page if it has not started yet.
 * Safe to call from anywhere, any number of times.
 */
export const loadFeatures = () => {
  if (started) return inFlight ?? Promise.resolve();
  started = true;
  return request();
};

/**
 * Turns one flag off for the rest of the page, for when an endpoint answers
 * `feature_disabled` (the operator turned it off after this page loaded).
 */
export const markFeatureDisabled = (name) => {
  if (!FLAG_NAMES.includes(name) || snapshot.disabled[name]) return;
  publish(makeSnapshot(snapshot.status, snapshot.flags, { ...snapshot.disabled, [name]: true }));
};

/** For `useSyncExternalStore`. */
export const subscribeFeatures = (listener) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** For `useSyncExternalStore`: `{status, flags}`, the same object until something changes. */
export const getFeaturesSnapshot = () => snapshot;

/** Whether `name` is on right now (false while loading and after a failure). */
export const isFeatureEnabled = (name) => snapshot.flags[name] === true;

/**
 * Tests only: answer as if the request had come back with `flags`, without
 * making one. Anything not named is off.
 */
export const __setFeaturesForTest = (flags = {}) => {
  started = true;
  publish(makeSnapshot(FeaturesStatus.READY, parseFeatures(flags)));
};

/** Tests only: back to a fresh page load. */
export const clearFeaturesCache = () => {
  generation += 1;
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
  inFlight = null;
  started = false;
  retries = 0;
  publish(makeSnapshot(FeaturesStatus.LOADING, ALL_OFF));
};
